import { useState } from "react";
import { styled, css } from "styled-components";
import { mobileBreakPointCSS } from "../../defaults/theme.js";

type Variant = "primary" | "secondary";

interface WrapperProps {
  $variant?: Variant;
  $hasValue?: boolean;
  $errorMessage?: string;
  $textarea: boolean;
}

const Wrapper = styled.div<{ $disabled: boolean }>`
  transition: opacity 0.1s ease-in-out;

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.5;
    `};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${(props) => props.theme.spacing.xs};
  font-weight: 600;
`;

const StyledInput = styled.input<WrapperProps>`
  height: 50px;
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "#fff" : "#fff"};
  color: var(--swatch2);
  border: 1px solid var(--swatch2);
  border-radius: 5px;
  padding: 0 0.75rem;
  outline: none;
  transition: border-color 0.1s ease-in-out;

  @media (min-width: ${mobileBreakPointCSS}) {
    min-width: 250px;
  }

  &:active,
  &:focus {
    border-color: var(--swatch1);
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  ${(props) =>
    props.$hasValue &&
    css`
      border-color: var(--swatch2);
    `};

  ${(props) =>
    !!props.$errorMessage &&
    css`
      border-color: var(--error);
    `};

  ${(props) =>
    props.$textarea &&
    css`
      padding: 0.75rem;
      height: 180px;
    `};
`;

const StatusWrapper = styled.div<{ $error?: boolean }>`
  display: flex;
  justify-content: flex-end;
  margin-top: calc(${(props) => props.theme.spacing.xs} / 2);

  ${(props) =>
    props.$error &&
    css`
      color: var(--error);
    `};
`;

interface Props
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  errorMessage?: string;
  label: string;
  textarea?: boolean;
  variant?: Variant;
}

const Input: React.FC<Props> = ({
  value,
  onChange,
  required = false,
  errorMessage = "",
  disabled = false,
  textarea = false,
  label,
  variant,
  ...restProps
}) => {
  const [val, setVal] = useState("");
  const hasValue = val !== "";

  return (
    <Wrapper $disabled={disabled}>
      <Label>
        {label} {required && "*"}
      </Label>
      {textarea ? (
        <StyledInput
          as="textarea"
          $hasValue={hasValue}
          $textarea={textarea}
          required={required}
          value={value}
          $errorMessage={errorMessage}
          $variant={variant}
          disabled={disabled}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            setVal(e.target.value);
            if (typeof onChange === "function") {
              onChange(e);
            }
          }}
          {...restProps}
        />
      ) : (
        <StyledInput
          as="input"
          $hasValue={hasValue}
          required={required}
          $textarea={textarea}
          value={value}
          $variant={variant}
          $errorMessage={errorMessage}
          disabled={disabled}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => {
            setVal(e.target.value);
            if (typeof onChange === "function") {
              onChange(e);
            }
          }}
          {...restProps}
        />
      )}
      {!!errorMessage && (
        <StatusWrapper className="b1" $error>
          {errorMessage}
        </StatusWrapper>
      )}
    </Wrapper>
  );
};

export { Input, Label };
